import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { DeletionPendingBanner } from "@/components/DeletionPendingBanner";
import { PrelaunchGate } from "@/components/PrelaunchGate";
import { AdminGate } from "@/components/AdminGate";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import {
  isAppDomain,
  isLandingDomain,
  isAppRoute,
  isMarketingRoute,
  getAppUrl,
  getMarketingUrl
} from "@/lib/domainConfig";
import Landing from "./pages/Landing";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import ScrollToTop from "@/components/ScrollToTop";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import { TourProvider } from "@/components/tour/TourContext";
import { SetupTourProvider } from "@/components/tour/SetupTourContext";
import { TourOverlay } from "@/components/tour/TourOverlay";
import { useLocaleSync } from "@/hooks/useLocaleSync";

const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const NewShoot = lazy(() => import("./pages/NewShoot"));
const ShootDetail = lazy(() => import("./pages/ShootDetail"));
const ClientBrief = lazy(() => import("./pages/ClientBrief"));
const Settings = lazy(() => import("./pages/Settings"));
const Pricing = lazy(() => import("./pages/Pricing"));
const MoodboardView = lazy(() => import("./pages/MoodboardView"));
const Demo = lazy(() => import("./pages/Demo"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Contact = lazy(() => import("./pages/Contact"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ClientBriefPage = lazy(() => import("./pages/ClientBriefPage"));
const FieldViewPage = lazy(() => import("./pages/FieldViewPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const AdminWaitlist = lazy(() => import("./pages/AdminWaitlist"));
const AdminMetrics = lazy(() => import("./pages/AdminMetrics"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const AdminBlogEditor = lazy(() => import("./pages/AdminBlogEditor"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NicheLandingPage = lazy(() => import("./pages/NicheLandingPage"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./blog/BlogPost"));

const queryClient = new QueryClient();

// Routes that should be blocked when user has pending deletion
const PROTECTED_FROM_DELETION = ['/dashboard', '/shoots', '/settings'];


function AppRoutes() {
  const { deletionInfo, loading, user } = useAuth();
  useLocaleSync();
  const location = useLocation();
  const host = typeof window === 'undefined' ? '' : window.location.hostname;
  const pathname = location.pathname;
  const isPreviewHost = host === 'localhost' || host.includes('.vercel.app');
  const shouldKeepPricingInApp = pathname === '/pricing' && (loading || Boolean(user));
  const redirectToAppUrl = !isPreviewHost && isLandingDomain() && isAppRoute(pathname)
    ? getAppUrl(pathname + location.search)
    : null;
  const redirectToMarketingUrl = !isPreviewHost && host === 'app.prepframe.com' && isMarketingRoute(pathname) && !shouldKeepPricingInApp
    ? getMarketingUrl(pathname + location.search)
    : null;

  // Hostname-based routing: redirect between domains based on route type
  // ONLY redirect app routes from marketing domain to app domain
  // Marketing pages should NEVER redirect to avoid SEO issues
  useEffect(() => {
    if (redirectToAppUrl) {
      window.location.replace(redirectToAppUrl);
      return;
    }

    if (redirectToMarketingUrl) {
      window.location.replace(redirectToMarketingUrl);
    }
  }, [redirectToAppUrl, redirectToMarketingUrl]);

  if (redirectToAppUrl || redirectToMarketingUrl) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show deletion pending banner for protected routes when user has pending deletion
  if (!loading && deletionInfo?.status === 'pending') {
    const isProtected = PROTECTED_FROM_DELETION.some(path => 
      location.pathname.startsWith(path)
    );
    if (isProtected) {
      return <DeletionPendingBanner />;
    }
  }

  return (
    <ErrorBoundary>
    <Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/photobizx" element={<Landing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/dashboard" element={<PrelaunchGate><Dashboard /></PrelaunchGate>} />
        <Route path="/shoots/new" element={<PrelaunchGate><NewShoot /></PrelaunchGate>} />
        <Route path="/shoots/:id" element={<PrelaunchGate><ShootDetail /></PrelaunchGate>} />
        <Route path="/shoots/:id/moodboard" element={<PrelaunchGate><MoodboardView /></PrelaunchGate>} />
        <Route path="/b/:token" element={<ClientBrief />} />
        <Route path="/settings" element={<PrelaunchGate><Settings /></PrelaunchGate>} />
        <Route path="/settings/profile" element={<Navigate to="/settings?tab=account" replace />} />
        <Route path="/settings/branding" element={<Navigate to="/settings?tab=branding" replace />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/client-brief" element={<ClientBriefPage />} />
        <Route path="/field-view" element={<FieldViewPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/admin/waitlist" element={<AdminGate><AdminWaitlist /></AdminGate>} />
        <Route path="/admin/metrics" element={<AdminGate><AdminMetrics /></AdminGate>} />
        <Route path="/admin/blog" element={<AdminGate><AdminBlog /></AdminGate>} />
        <Route path="/admin/blog/new" element={<AdminGate><AdminBlogEditor /></AdminGate>} />
        <Route path="/admin/blog/:id" element={<AdminGate><AdminBlogEditor /></AdminGate>} />
        <Route path="/wedding-photography-brief" element={<NicheLandingPage nicheKey="wedding" />} />
        <Route path="/portrait-photography-brief" element={<NicheLandingPage nicheKey="portrait" />} />
        <Route path="/newborn-photography-brief" element={<NicheLandingPage nicheKey="newborn" />} />
        <Route path="/commercial-photography-brief" element={<NicheLandingPage nicheKey="commercial" />} />
        <Route path="/real-estate-photography-brief" element={<NicheLandingPage nicheKey="real_estate" />} />
        <Route path="/event-photography-brief" element={<NicheLandingPage nicheKey="event" />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem={false}
      disableTransitionOnChange={true}
    >
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <TourProvider>
              <SetupTourProvider>
                <ScrollToTop />
                <AnalyticsProvider />
                <CookieConsentBanner />
                <TourOverlay />
                <AppRoutes />
              </SetupTourProvider>
            </TourProvider>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
